// Call speed check (Settings › Call speed check): where every part of a call is handled, on a map, with
// measured delays and plain-language findings. Built to answer "is a setting sending audio on an extra trip?".
//
// The parts of a call:
//   You ──(app audio)──▶ Telnyx media server (picked by the calling region + the media site on the logins)
//   Telnyx ──(customer side)──▶ media site on the Call Control app ──▶ phone network
//   App server (Fly) only sets calls up (ringing, answering); call audio never goes through it.

export interface Place {
  name: string;
  lat: number;
  lon: number;
}

/** Telnyx media sites (anchorsite names, as Telnyx spells them). */
export const MEDIA_SITE_PLACES: Record<string, Place> = {
  "San Jose, CA": { name: "San Jose", lat: 37.34, lon: -121.89 },
  "Vancouver, Canada": { name: "Vancouver", lat: 49.28, lon: -123.12 },
  "Chicago, IL": { name: "Chicago", lat: 41.88, lon: -87.63 },
  "Ashburn, VA": { name: "Ashburn", lat: 39.04, lon: -77.49 },
  "Toronto, Canada": { name: "Toronto", lat: 43.65, lon: -79.38 },
  "Montreal, Canada": { name: "Montreal", lat: 45.5, lon: -73.57 },
  "London, UK": { name: "London", lat: 51.51, lon: -0.13 },
  "Amsterdam, Netherlands": { name: "Amsterdam", lat: 52.37, lon: 4.9 },
  "Frankfurt, Germany": { name: "Frankfurt", lat: 50.11, lon: 8.68 },
  "Sydney, Australia": { name: "Sydney", lat: -33.87, lon: 151.21 },
};

/** Telnyx calling regions (the app's connection). Approximate city for each; Telnyx doesn't publish exact ones. */
export const REGION_PLACES: Record<string, Place> = {
  "us-west": { name: "US West (California)", lat: 37.34, lon: -121.89 },
  "us-central": { name: "US Central (Chicago)", lat: 41.88, lon: -87.63 },
  "us-east": { name: "US East (Virginia)", lat: 39.04, lon: -77.49 },
  "ca-central": { name: "Canada (Toronto)", lat: 43.65, lon: -79.38 },
  eu: { name: "Europe", lat: 52.37, lon: 4.9 },
};

/** Fly.io region codes (where the app server runs). */
export const FLY_PLACES: Record<string, Place> = {
  sjc: { name: "San Jose", lat: 37.34, lon: -121.89 },
  lax: { name: "Los Angeles", lat: 34.05, lon: -118.24 },
  sea: { name: "Seattle", lat: 47.61, lon: -122.33 },
  ord: { name: "Chicago", lat: 41.88, lon: -87.63 },
  dfw: { name: "Dallas", lat: 32.78, lon: -96.8 },
  iad: { name: "Ashburn", lat: 39.04, lon: -77.49 },
  ewr: { name: "Secaucus NJ", lat: 40.79, lon: -74.06 },
  yyz: { name: "Toronto", lat: 43.65, lon: -79.38 },
  yul: { name: "Montreal", lat: 45.5, lon: -73.57 },
  den: { name: "Denver", lat: 39.74, lon: -104.99 },
  mia: { name: "Miami", lat: 25.76, lon: -80.19 },
  lhr: { name: "London", lat: 51.51, lon: -0.13 },
  ams: { name: "Amsterdam", lat: 52.37, lon: 4.9 },
  fra: { name: "Frankfurt", lat: 50.11, lon: 8.68 },
};

/** Great-circle distance, km. */
export function km(a: Place, b: Place): number {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(x)));
}
/** The least a round trip over that distance can take on fiber (~200 km per ms one way), ms. */
export const minRttMs = (distanceKm: number) => Math.round((distanceKm * 2) / 200);

export interface GeoIp extends Place {
  ip: string;
  org: string;
}
const geoCache = new Map<string, Promise<GeoIp | null>>();
/** City for an IP address (or this device's own when ip is ""). Two free lookup services, first that answers. */
export function geoip(ip = ""): Promise<GeoIp | null> {
  const hit = geoCache.get(ip);
  if (hit) return hit;
  const p = (async () => {
    const tries: [string, (j: any) => GeoIp | null][] = [
      [`https://ipapi.co/${ip ? `${ip}/` : ""}json/`, (j) => (typeof j?.latitude === "number" ? { ip: j.ip, name: [j.city, j.region_code || j.region].filter(Boolean).join(", "), lat: j.latitude, lon: j.longitude, org: j.org ?? "" } : null)],
      [`https://ipwho.is/${ip}`, (j) => (j?.success && typeof j.latitude === "number" ? { ip: j.ip, name: [j.city, j.region_code || j.region].filter(Boolean).join(", "), lat: j.latitude, lon: j.longitude, org: j.connection?.org ?? "" } : null)],
    ];
    for (const [url, read] of tries) {
      try {
        const r = await fetch(url, { signal: AbortSignal.timeout(5000) });
        if (!r.ok) continue;
        const g = read(await r.json());
        if (g) return g;
      } catch {
        // next service
      }
    }
    return null;
  })();
  geoCache.set(ip, p);
  return p;
}

/**
 * Telnyx's media addresses (sip.telnyx.com/voice.json, May 2026). IP-to-city services place all of these in
 * Chicago (Telnyx's head office), whatever site really carries the audio, so their city is never trusted.
 */
const TELNYX_MEDIA_CIDRS = [
  "36.255.198.128/25", "50.114.136.128/25", "50.114.144.0/21", "64.16.226.0/24", "64.16.227.0/24", "64.16.228.0/24",
  "64.16.229.0/24", "64.16.230.0/24", "64.16.248.0/24", "64.16.249.0/24", "64.16.246.96/27", "64.16.246.128/26",
  "103.115.244.128/25", "103.115.247.0/24", "185.246.41.128/25", "185.246.42.128/28", "192.76.120.0/24", "64.16.250.0/24",
];
const ipNum = (ip: string) => {
  const m = /^(\d+)\.(\d+)\.(\d+)\.(\d+)$/.exec(ip);
  return m ? ((+m[1] << 24) >>> 0) + (+m[2] << 16) + (+m[3] << 8) + +m[4] : null;
};
export function isTelnyxMediaIp(ip: string): boolean {
  const n = ipNum(ip);
  if (n == null) return false;
  return TELNYX_MEDIA_CIDRS.some((c) => {
    const [base, bits] = c.split("/");
    const b = ipNum(base)!;
    const mask = bits === "0" ? 0 : (~0 << (32 - Number(bits))) >>> 0;
    return ((n & mask) >>> 0) === ((b & mask) >>> 0);
  });
}

export interface DeviceDelay {
  /** Speaker / headset: time from the app to the sound coming out, ms (null = this browser can't tell). */
  outMs: number | null;
  /** Microphone: time from the sound to the app, ms (null = can't tell). */
  inMs: number | null;
  outLabel: string;
  /** The speaker or mic looks like a Bluetooth headset (they add the most delay of anything on the device). */
  bluetooth: boolean;
}
const BT = /airpods|bluetooth|hands-?free|buds|beats|bose|wh-1000|jabra|plantronics|poly |headset \(bt\)|\bbt\b/i;

/** Measures this device's own audio delay: speaker / headset output, and the mic (when mic access is already allowed). */
export async function measureDeviceDelay(speakerId: string, micId: string, askMic: boolean): Promise<DeviceDelay> {
  const out: DeviceDelay = { outMs: null, inMs: null, outLabel: "", bluetooth: false };
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const spk = devices.find((d) => d.kind === "audiooutput" && d.deviceId === (speakerId || "default"));
    const mic = devices.find((d) => d.kind === "audioinput" && d.deviceId === (micId || "default"));
    out.outLabel = spk?.label ?? "";
    out.bluetooth = BT.test(spk?.label ?? "") || BT.test(mic?.label ?? "");
  } catch {
    // no labels
  }
  try {
    const ctx = new AudioContext({ latencyHint: "interactive" });
    try {
      if (speakerId && "setSinkId" in ctx) await (ctx as any).setSinkId(speakerId);
    } catch {
      // default speaker
    }
    await ctx.resume().catch(() => {});
    const src = ctx.createConstantSource();
    const g = ctx.createGain();
    g.gain.value = 0;
    src.connect(g).connect(ctx.destination);
    src.start();
    await new Promise((r) => setTimeout(r, 500));
    const o = (ctx as any).outputLatency;
    if (ctx.state === "running" && typeof o === "number" && o > 0) out.outMs = Math.round((o + (ctx.baseLatency || 0)) * 1000);
    void ctx.close();
  } catch {
    // no Web Audio
  }
  if (askMic) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: { exact: micId } } : true });
      const lat = (stream.getAudioTracks()[0]?.getSettings() as { latency?: number }).latency;
      if (typeof lat === "number" && lat > 0) out.inMs = Math.round(lat * 1000);
      for (const t of stream.getTracks()) t.stop();
    } catch {
      // no mic access
    }
  }
  return out;
}

// ------------------------------------------------------------------ the map (Leaflet, loaded only on this page)

declare const L: any;
let leafletLoading: Promise<boolean> | null = null;
function loadLeaflet(): Promise<boolean> {
  if ((window as any).L) return Promise.resolve(true);
  leafletLoading ??= new Promise<boolean>((resolve) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "/vendor/leaflet/leaflet.css";
    document.head.append(css);
    const js = document.createElement("script");
    js.src = "/vendor/leaflet/leaflet.js";
    js.onload = () => resolve(true);
    js.onerror = () => resolve(false);
    document.head.append(js);
  });
  return leafletLoading;
}

export interface MapPin extends Place {
  kind: "you" | "region" | "site" | "media" | "server" | "other";
  label: string;
}
export interface MapLine {
  from: Place;
  to: Place;
  kind: "app" | "customer" | "setup";
  label?: string;
}
const COLORS: Record<MapPin["kind"] | MapLine["kind"], string> = {
  you: "#4fc0dc", region: "#a78bfa", site: "#f2c46d", media: "#3fc48c", server: "#9aa0ad", other: "#5b6170",
  app: "#3fc48c", customer: "#f2c46d", setup: "#9aa0ad",
};

let basemap: Promise<unknown | null> | null = null;
function loadBasemap() {
  basemap ??= fetch("/map/basemap.json").then((r) => (r.ok ? r.json() : null)).catch(() => null);
  return basemap;
}

/** Draws (or redraws) the map into `el`. Returns false if the map library couldn't load. */
export async function drawMap(el: HTMLElement, pins: MapPin[], lines: MapLine[]): Promise<boolean> {
  if (!(await loadLeaflet())) return false;
  // Get the shapes first: nothing may be added to a map before it has a view, and a newer draw may start meanwhile.
  const shapes = await loadBasemap();
  if (!el.isConnected) return true;
  const old = (el as any)._map;
  if (old) old.remove();
  // 7.10.30: no animations. Leaving the page while it zoomed to fit threw "_leaflet_pos" errors (the map was gone
  // mid-animation); a small static map doesn't need them.
  const map = L.map(el, { zoomControl: true, attributionControl: true, scrollWheelZoom: false, zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false }).setView([40, -98], 3);
  (el as any)._map = map;
  // Our own map (country outlines and US state borders, public domain), served by Joy Phone: no map service,
  // no API key, nothing sent anywhere. Drawn as shapes, so it stays sharp at any zoom.
  map.attributionControl.setPrefix(false);
  map.attributionControl.addAttribution("Natural Earth · US Census");
  if (shapes) {
    L.geoJSON(shapes, {
      interactive: false,
      style: (f: any) => f?.properties?.kind === "state-borders"
        ? { color: "#3a4150", weight: 0.8, fill: false }
        : { color: "#4a5263", weight: 1, fillColor: "#232833", fillOpacity: 1 },
    }).addTo(map);
  }
  for (const l of lines) {
    L.polyline([[l.from.lat, l.from.lon], [l.to.lat, l.to.lon]], {
      color: COLORS[l.kind], weight: l.kind === "setup" ? 2 : 4, opacity: 0.9, dashArray: l.kind === "setup" ? "4 6" : undefined,
    }).addTo(map).bindTooltip(l.label ?? "", { sticky: true });
  }
  const bounds: [number, number][] = [];
  for (const p of pins) {
    const main = p.kind !== "other";
    L.circleMarker([p.lat, p.lon], {
      radius: main ? 8 : 5, color: "#0b0d12", weight: 2, fillColor: COLORS[p.kind], fillOpacity: main ? 1 : 0.55,
    }).addTo(map).bindTooltip(p.label, {
      permanent: main, className: `speed-tip ${p.kind}`,
      // Spread the labels out: places in the same city would otherwise sit on top of each other.
      direction: p.kind === "region" ? "top" : p.kind === "server" ? "bottom" : p.kind === "site" ? "left" : "right",
      offset: p.kind === "region" ? [0, -8] : p.kind === "server" ? [0, 8] : p.kind === "site" ? [-8, 0] : [8, 0],
    });
    if (main) bounds.push([p.lat, p.lon]);
  }
  if (bounds.length > 1) map.fitBounds(bounds, { paddingTopLeft: [170, 50], paddingBottomRight: [230, 50], maxZoom: 6, animate: false });
  else map.setView(bounds[0] ?? [40, -98], 4, { animate: false });
  setTimeout(() => {
    if (!el.isConnected) {
      // Left the page already: let the map go instead of resizing one that's no longer shown.
      map.remove();
      if ((el as any)._map === map) (el as any)._map = null;
      return;
    }
    map.invalidateSize({ animate: false });
  }, 50);
  return true;
}
